// taken from https://raw.githubusercontent.com/donmccurdy/expression-eval/main/index.ts
// -------------------
// THIS FILE SHOULD BE ALMOST EQUIVALENT TO expression-visitor.ts (SAME FOLDER)
// IF YOU CHANGE SOMETHING HERE, MAKE SURE TO ADJUST THE OTHER FILE AS WELL
// -------------------
import jsep, { CoreExpression } from 'jsep';
import { localize } from './locale';
const binops: Record<string, (a: any, b: any) => any> = {
  '||': function (a: any, b: any) {
    return a || b;
  },
  '&&': function (a: any, b: any) {
    return a && b;
  },
  '|': function (a: any, b: any) {
    return a | b;
  },
  '^': function (a: any, b: any) {
    return a ^ b;
  },
  '&': function (a: any, b: any) {
    return a & b;
  },
  '==': function (a: any, b: any) {
    return a == b;
  }, // jshint ignore:line
  '!=': function (a: any, b: any) {
    return a != b;
  }, // jshint ignore:line
  '===': function (a: any, b: any) {
    return a === b;
  },
  '!==': function (a: any, b: any) {
    return a !== b;
  },
  '<': function (a: any, b: any) {
    return a < b;
  },
  '>': function (a: any, b: any) {
    return a > b;
  },
  '<=': function (a: any, b: any) {
    return a <= b;
  },
  '>=': function (a: any, b: any) {
    return a >= b;
  },
  '<<': function (a: any, b: any) {
    return a << b;
  },
  '>>': function (a: any, b: any) {
    return a >> b;
  },
  '>>>': function (a: any, b: any) {
    return a >>> b;
  },
  '+': function (a: any, b: any) {
    return a + b;
  },
  '-': function (a: any, b: any) {
    return a - b;
  },
  '*': function (a: any, b: any) {
    return a * b;
  },
  '/': function (a: any, b: any) {
    return a / b;
  },
  '%': function (a: any, b: any) {
    return a % b;
  },
};

const unops: Record<string, (a: any) => any> = {
  '-': function (a: any) {
    return -a;
  },
  '+': function (a: any) {
    return +a;
  },
  '~': function (a: any) {
    return ~a;
  },
  '!': function (a: any) {
    return !a;
  },
};

declare type operand = number | string;
declare type unaryCallback = (a: operand) => operand;
declare type binaryCallback = (a: operand, b: operand) => operand;

function evaluateArray(list: jsep.Expression[], context: any) {
  return list.map(function (v) {
    return evaluate(toCoreExpression(v), context);
  });
}

function evaluateMember(node: jsep.MemberExpression, context: any) {
  const object = evaluate(toCoreExpression(node.object), context);
  let key: string;
  if (node.computed) {
    key = evaluate(toCoreExpression(node.property), context);
  } else {
    key = (node.property as jsep.Identifier).name;
  }
  if (/^__proto__|prototype|constructor$/.test(key)) {
    throw Error(`Access to member "${key}" disallowed.`);
  }
  if (Array.isArray(object)) {
    /*
    If we are calling MemberExpression on Array, let's call it on first element instead

    The use case: if there's a Custom Field of Dictionary type, it could contain several values.
    Configuration regarding single vs multiple value is done within UI editor, so on Model level we always store Dictionary fields as arrays.
    So, e.g. for RefDoctor in Case field, we receive the following data:
    {
      case: { 'Additional doctor': [ {id: 1, value: 'DOC1', values: {Id: 1, ShortName: 'blablabla', Title: 'zxc', Name: 'DOC1'} } ]
    }
     */
    if (key === '' || key === undefined || key === null) {
      throw new Error(
        `Access to member with null/empty key is disallowed. Key: "${key}"`
      );
    }
    if (object.length > 1) {
      return [object, object.map((x) => x[key]).join(', ')];
    } else {
      const result = injectToStringMethodToDictionaryValues(object[0][key]);
      return [object, result];
    }
  } else {
    if (object === null || object === undefined) {
      return [undefined, undefined];
    }
    const result = injectToStringMethodToDictionaryValues(object[key]);
    return [object, result];
  }
}

type BinopsKey = keyof typeof binops;
type UnopsKey = keyof typeof unops;

export function evaluateParsedExpression(
  expression: jsep.Expression,
  context: any
) {
  return evaluate(toCoreExpression(expression), context);
}

export function evaluateExpression(expression: string, context: any) {
  if (expression === '') {
    // we return empty strings for empty expressions to support adding empty cells for verticalTable/horizontalTable
    return '';
  }

  const parsedExpression = jsep(expression);

  return evaluate(toCoreExpression(parsedExpression), context);
}

function evaluate(node: CoreExpression, context: any): any {
  switch (node.type) {
    case 'ArrayExpression':
      return evaluateArray(node.elements, context);

    case 'BinaryExpression':
      return binops[node.operator as BinopsKey](
        evaluate(toCoreExpression(node.left), context),
        evaluate(toCoreExpression(node.right), context)
      );

    case 'CallExpression':
      let caller, fn, assign;
      if (node.callee.type === 'MemberExpression') {
        assign = evaluateMember(node.callee as jsep.MemberExpression, context);
        caller = assign[0];
        fn = assign[1];
      } else {
        fn = evaluate(toCoreExpression(node.callee), context);
      }
      if (typeof fn !== 'function') {
        return undefined;
      }
      const args = evaluateArray(node.arguments, context);
      return fn.apply(caller, args);

    case 'ConditionalExpression':
      return evaluate(toCoreExpression(node.test), context)
        ? evaluate(toCoreExpression(node.consequent), context)
        : evaluate(toCoreExpression(node.alternate), context);

    case 'Identifier':
      return context[node.name];

    case 'Literal':
      return node.value;

    case 'MemberExpression':
      return evaluateMember(node, context)[1];

    case 'ThisExpression':
      return context;

    case 'Compound':
      return context;

    case 'UnaryExpression':
      return unops[node.operator as UnopsKey](
        evaluate(toCoreExpression(node.argument), context)
      );

    default:
      return;
  }
}

function toCoreExpression(expression: jsep.Expression) {
  return expression as CoreExpression;
}

/*
  Function injects `.toString()` method to all dictionary values.
  E.g. dictionary value like:
    {"id":73,"value":"Insur 123","isDeleted":false,"values":{"zxczxc":"qwerqwer","address":"asdfasdf"}}
  will now have .toString() method that will return the `value` field.
   */
export function injectToStringMethodToDictionaryValues(value: any) {
  if (Array.isArray(value)) {
    value.forEach((x) => {
      injectToStringMethodToDictionaryValues(x);
    });
    value.toString = function () {
      return this.map((x) => x.toString()).join(', ');
    };
  } else {
    if (value === undefined || value === null) {
      return '';
    } else if (typeof value === 'string' && value.startsWith('dictionaries.')) {
      value = localize(value);
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
    } else if (typeof value === 'object') {
      if (!value.toString || value.toString === Object.prototype.toString) {
        value.toString = toStringOverride;
      }
      Object.values(value).forEach((v) => {
        if (typeof v === 'object') {
          injectToStringMethodToDictionaryValues(v);
        }
      });
    } else {
      value.toString = function () {
        return this;
      };
    }
  }
  return value;
}

function toStringOverride(this: any) {
  if (this.Value !== undefined) {
    const value = this.Value;
    if (typeof value === 'string' && value.startsWith('dictionaries.')) {
      return localize(value);
    } else {
      return value;
    }
  } else {
    return this;
  }
}
export { jsep as parse };
